import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  Divider,
  Grid,
  Group,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import {
  AlertTriangle,
  Code2,
  Copy,
  Crosshair,
  Layers,
  Network,
  ShieldAlert,
  Sparkles,
  Target,
} from 'lucide-react';
import { Suspense, lazy, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Badge,
  Button,
  Card,
  Modal,
  PageContainer,
  PageHeader,
  Pill,
  Skeleton,
} from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { useScenario } from '../hooks/useScenarios';
import { useCloneBlueprint } from '../hooks/useBlueprints';
import { PhaseBadge } from '../components/blueprints/BlueprintCanvas';
import { colors } from '../theme/tokens';
import type { Difficulty } from '../api/types';

const BlueprintCanvas = lazy(() =>
  import('../components/blueprints/BlueprintCanvas').then((m) => ({ default: m.BlueprintCanvas })),
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/blueprints/$slug')({
  component: BlueprintDetail,
});

const DIFFICULTY_VARIANT: Record<
  Difficulty,
  'success' | 'info' | 'warn' | 'danger'
> = {
  beginner: 'success',
  intermediate: 'info',
  advanced: 'warn',
  expert: 'danger',
};

const SEVERITY_VARIANT: Record<string, 'neutral' | 'info' | 'success' | 'warn' | 'danger'> = {
  informational: 'neutral',
  low: 'info',
  medium: 'warn',
  high: 'danger',
  critical: 'danger',
};

function BlueprintDetail() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { slug } = (Route as any).useParams();
  const { data, isPending, isError, error } = useScenario(slug);
  const [cloneOpen, setCloneOpen] = useState(false);

  if (isPending) {
    return (
      <PageContainer size="wide">
        <Stack gap="lg">
          <Skeleton height={32} width="40%" />
          <Skeleton height={520} radius="md" />
        </Stack>
      </PageContainer>
    );
  }
  if (isError || !data) {
    return (
      <PageContainer size="wide">
        <Alert
          icon={<AlertTriangle size={16} />}
          color="red"
          variant="light"
          title="Could not load blueprint"
        >
          {(error as Error)?.message ?? 'Blueprint not found.'}
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Layers}
        title={data.name}
        description="Blueprint composition and deploy options."
        breadcrumbs={[
          { label: 'Forge', to: '/' },
          { label: 'Blueprints', to: '/blueprints' },
          { label: 'Detail' },
        ]}
        actions={
          <>
            <Badge variant={DIFFICULTY_VARIANT[data.difficulty]}>{data.difficulty}</Badge>
            <Badge variant="neutral">v{data.version}</Badge>
            <Button
              variant="primary"
              leftIcon={<Copy size={14} />}
              onClick={() => setCloneOpen(true)}
            >
              Duplicate to my tenant
            </Button>
          </>
        }
      />

      <Text c="dimmed" size="sm" maw={780} mb="lg">
        {data.description}
      </Text>

      <Grid gutter="md" mb="lg">
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Metric icon={<Layers size={16} />} label="VMs" value={String(data.vmCount)} />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Metric icon={<Network size={16} />} label="Segments" value={String(data.segments.length || 1)} />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Metric icon={<Target size={16} />} label="Phases" value={String(data.phases.length)} />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Metric icon={<Crosshair size={16} />} label="Objectives" value={String(data.objectives.length)} />
        </Grid.Col>
      </Grid>

      <Tabs defaultValue="visual" variant="default" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="visual" leftSection={<Network size={14} />}>
            Visual
          </Tabs.Tab>
          <Tabs.Tab value="phases" leftSection={<Target size={14} />}>
            Attack chain
          </Tabs.Tab>
          <Tabs.Tab value="detections" leftSection={<ShieldAlert size={14} />}>
            Detections
          </Tabs.Tab>
          <Tabs.Tab value="yaml" leftSection={<Code2 size={14} />}>
            YAML source
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="visual" pt="lg">
          <Suspense
            fallback={<Skeleton height={520} radius="md" />}
          >
            <BlueprintCanvas detail={data} height={560} />
          </Suspense>
          <Stack gap={4} mt="md">
            <Text size="xs" c="dimmed" ff="JetBrains Mono">
              Network: {data.networkCidr ?? '10.{range_id}.0.0/24'} ·{' '}
              routing {data.networkRoutingPattern ?? 'native'} ·{' '}
              egress {data.networkEgress ?? 'isolated'}
            </Text>
            <Text size="xs" c="dimmed">
              Pan + zoom to explore. VMs are grouped by network segment. Animated
              edges show the attacker → target flow per phase.
            </Text>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="phases" pt="lg">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="show"
            className="flex flex-col gap-3"
          >
            {data.phases.map((phase, idx) => (
              <motion.div key={phase.id} variants={cardItem}>
              <Card>
                <Stack gap="sm">
                  <PhaseBadge
                    index={idx}
                    label={phase.name}
                    {...(phase.technique ? { technique: phase.technique } : {})}
                  />
                  <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                    {phase.description}
                  </Text>
                  <Group gap={6} wrap="wrap">
                    {phase.targetVms.length > 0 ? (
                      <>
                        <Text size="xs" c="dimmed">
                          targets:
                        </Text>
                        {phase.targetVms.map((t) => (
                          <Pill key={t} size="xs" variant="info">
                            {t}
                          </Pill>
                        ))}
                      </>
                    ) : null}
                  </Group>
                  {phase.expectedDetections.length > 0 ? (
                    <Group gap={6} wrap="wrap">
                      <Text size="xs" c="dimmed">
                        expected detections:
                      </Text>
                      {phase.expectedDetections.map((d) => (
                        <Pill key={d} size="xs" variant="warn">
                          {d}
                        </Pill>
                      ))}
                    </Group>
                  ) : null}
                  {phase.dependsOn.length > 0 ? (
                    <Text size="xs" c="dimmed" ff="JetBrains Mono">
                      depends on: {phase.dependsOn.join(' · ')}
                    </Text>
                  ) : null}
                </Stack>
              </Card>
              </motion.div>
            ))}
          </motion.div>
        </Tabs.Panel>

        <Tabs.Panel value="detections" pt="lg">
          <Stack gap="md">
            {data.detections.length === 0 ? (
              <Text c="dimmed" size="sm">
                This blueprint declares no detections.
              </Text>
            ) : (
              data.detections.map((det) => (
                <Card key={det.sigmaRuleId}>
                  <Stack gap="xs">
                    <Group gap="sm" justify="space-between" align="center">
                      <Text ff="JetBrains Mono" fw={700} size="sm">
                        {det.sigmaRuleId}
                      </Text>
                      <Pill
                        size="xs"
                        variant={SEVERITY_VARIANT[det.severity] ?? 'neutral'}
                      >
                        {det.severity}
                      </Pill>
                    </Group>
                    <Text size="xs" c="dimmed" ff="JetBrains Mono">
                      fires from phase{det.phaseIds.length === 1 ? '' : 's'}:{' '}
                      {det.phaseIds.join(' · ') || '—'} · expected match count{' '}
                      {det.expectedMatchCount}
                    </Text>
                  </Stack>
                </Card>
              ))
            )}
            <Divider />
            <Stack gap="xs">
              <Text size="xs" c="dimmed" tt="uppercase">
                Objectives → detection coverage
              </Text>
              {data.objectives.map((obj) => (
                <Card key={obj.id}>
                  <Stack gap={6}>
                    <Group justify="space-between" gap="xs">
                      <Text fw={600} size="sm">
                        {obj.title}
                      </Text>
                      <Group gap={4}>
                        <Pill size="xs" variant="info">
                          {obj.points} pts
                        </Pill>
                        <Pill size="xs" variant={obj.type === 'manual' ? 'warn' : 'success'}>
                          {obj.type}
                        </Pill>
                      </Group>
                    </Group>
                    {obj.description ? (
                      <Text size="xs" c="dimmed">
                        {obj.description}
                      </Text>
                    ) : null}
                    {obj.detectionIds.length > 0 ? (
                      <Group gap={4} wrap="wrap">
                        {obj.detectionIds.map((d) => (
                          <Pill key={d} size="xs" variant="neutral">
                            {d}
                          </Pill>
                        ))}
                      </Group>
                    ) : null}
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="yaml" pt="lg">
          <YamlView document={data.document} />
        </Tabs.Panel>
      </Tabs>

      <CloneDialog
        open={cloneOpen}
        onClose={() => setCloneOpen(false)}
        sourceSlug={data.slug}
        sourceName={data.name}
      />
    </PageContainer>
  );
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Card>
      <Stack gap={2}>
        <Group gap={6}>
          <span style={{ color: colors.hudCyan }}>{icon}</span>
          <Text size="xs" c="dimmed" tt="uppercase">
            {label}
          </Text>
        </Group>
        <Text fw={700} ff="JetBrains Mono" size="lg" c="rgb(var(--c-txt-bright))">
          {value}
        </Text>
      </Stack>
    </Card>
  );
}

function YamlView({ document }: { document: Record<string, unknown> | undefined }) {
  const yaml = useMemo(() => safeYaml(document), [document]);
  if (!yaml) {
    return (
      <Text c="dimmed" size="sm">
        No YAML source available for this blueprint.
      </Text>
    );
  }
  return (
    <ScrollArea h={560} type="auto">
      <Card style={{ padding: 0 }}>
        <pre
          style={{
            margin: 0,
            padding: 16,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.55,
            color: '#d4d4d8',
            whiteSpace: 'pre',
            overflow: 'auto',
          }}
        >
          {yaml}
        </pre>
      </Card>
    </ScrollArea>
  );
}

function safeYaml(doc: Record<string, unknown> | undefined): string {
  if (!doc) return '';
  return jsonToYaml(doc, 0);
}

function jsonToYaml(value: unknown, indent: number): string {
  const pad = '  '.repeat(indent);
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return value.includes('\n') ? `|\n${value.split('\n').map((l) => `${pad}  ${l}`).join('\n')}` : safeString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return value.map((item) => {
      if (typeof item === 'object' && item !== null) {
        const inner = jsonToYaml(item, indent + 1).replace(new RegExp(`^${pad}  `), '');
        return `${pad}- ${inner}`;
      }
      return `${pad}- ${jsonToYaml(item, 0)}`;
    }).join('\n');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '{}';
    return entries.map(([k, v]) => {
      if (Array.isArray(v) || (typeof v === 'object' && v !== null)) {
        return `${pad}${k}:\n${jsonToYaml(v, indent + 1)}`;
      }
      return `${pad}${k}: ${jsonToYaml(v, 0)}`;
    }).join('\n');
  }
  return String(value);
}

function safeString(s: string): string {
  if (/^[\w./@:_-]+$/.test(s)) return s;
  return `'${s.replace(/'/g, "''")}'`;
}

interface CloneDialogProps {
  open: boolean;
  onClose: () => void;
  sourceSlug: string;
  sourceName: string;
}

function CloneDialog({ open, onClose, sourceSlug, sourceName }: CloneDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const clone = useCloneBlueprint();

  const submit = async () => {
    setError(null);
    try {
      await clone.mutateAsync({
        sourceSlug,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      onClose();
      setName('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Clone failed');
    }
  };

  return (
    <Modal
      opened={open}
      onClose={() => {
        setName('');
        setError(null);
        onClose();
      }}
      title={
        <Group gap="xs">
          <Sparkles size={16} color={colors.neon} />
          <Text fw={600}>Duplicate blueprint</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Make an editable copy of <b>{sourceName}</b> inside your tenant.
          Topology, attack chain, and detections carry over; you can revise
          them in the Scenario Builder.
        </Text>
        <TextInput
          label="Name for your copy"
          placeholder={`${sourceName} (copy)`}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          disabled={clone.isPending}
        />
        {error ? (
          <Alert color="red" variant="light" icon={<AlertTriangle size={14} />}>
            {error}
          </Alert>
        ) : null}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={clone.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={clone.isPending}
            leftIcon={<Copy size={14} />}
          >
            Duplicate
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
